import React, { useEffect, useState, useCallback, useRef } from 'react'

// types
import {
  IEcommerceAnswerSchema,
  IEcommerceQuestionSchema,
  IFileAsset,
  IEcommerceVideoAsset,
  ASSETS_TYPES
} from '@aytm/app/swidget/base/questions/Ecommerce/types'
import { IImageAsset } from '@aytm/app/swidget/base/modules/images/types'

// functions
import { applyFilter } from '@aytm/app/swidget/base/questions/Ecommerce/utils/applyFilter.ts'
import { defaultAssetIndex } from '@aytm/app/swidget/base/questions/Ecommerce/utils/defaultAssetIndex.ts'
import { formatPriceWithLocale } from '@aytm/app/swidget/base/questions/Ecommerce/utils/formatPriceWithLocale.ts'
import { getAssetType } from '@aytm/app/swidget/base/questions/Ecommerce/utils/getAssetType.ts'
import { getBigAssetPath, getSmallAssetPath } from '@aytm/lib/common/assets.ts'
import { hapticEffects } from '@aytm/app/swidget/base/lib/hapticEffects.ts'

// components
import '@aytm/app/swidget/base/questions/Ecommerce/Ecommerce/Store/ModelViewer.ts'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink.tsx'
import Rating from './Rating.tsx'
import SubmitModal from './SubmitModal.tsx'
import Carousel from '@aytm/app/swidget/base/questions/Ecommerce/components/Carousel.tsx'

// custom hooks
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import useOnClickOutside from '@aytm/lib/hooks/useOnClickOutside.ts'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale.ts'
import useTimeline from '@aytm/app/swidget/base/modules/timeline/useTimeline.ts'
import useAssetsCollectionBuilder from '../../hooks/useAssetsCollectionBuilder.ts'

// styles
import style from './Product.module.scss'
import nikeStyle from '../Nike.module.scss'

// constants
const FOOTWEAR = [
  '5',
  '5.5',
  '6',
  '6.5',
  '7',
  '7.5',
  '8',
  '8.5',
  '9',
  '9.5',
  '10',
  '10.5',
  '11',
  '11.5',
  '12',
  '12.5',
  '13'
]
const CLOTHING = [
  'XS',
  'S',
  'S Tall',
  'M',
  'M Tall',
  'L',
  'L Tall',
  'XL',
  'XL Tall',
  'XXL',
  'XXL Tall',
  '3XL',
  '3XL Tall',
  '4XL',
  '4XL Tall'
]

interface IProductProps {
  answer: IEcommerceAnswerSchema
  schema: IEcommerceQuestionSchema
  quantity: number
  index: number
  totals: number[]
  findability: boolean
  onAddToCart: (qnt: number, index: number) => void
  onCartOpen: () => void
  scrollTop: () => void
  checkout: () => void
}

const Product = ({
  answer,
  schema,
  quantity,
  index,
  totals,
  findability,
  onAddToCart,
  onCartOpen,
  scrollTop,
  checkout
}: IProductProps) => {
  // custom hooks calls
  const { t, languageTag } = useSurveyLocale()
  const viewport = useViewport()
  const timeline = useTimeline()

  // refs
  const mainImageRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)

  // memoized
  const assetsCollection = useAssetsCollectionBuilder(answer)

  // state
  const [showModal, setShowModal] = useState<boolean>(false)
  const [onImage, setOnImage] = useState<boolean>(false)
  const [position, setPosition] = useState<null | { x: number; y: number }>(null)
  const [isPlaying, setIsPlaying] = useState<boolean>(true)
  const [selectedAssetIndex, setSelectedAssetIndex] = useState<number>(() => defaultAssetIndex(assetsCollection))

  // functions
  const togglePlayPause = useCallback(async () => {
    if (videoRef.current) {
      isPlaying ? videoRef.current.pause() : await videoRef.current.play()
      setIsPlaying(!isPlaying)
    }
  }, [isPlaying])

  const playingIndexRef = useRef<number | null>(null)

  const logVideoEvent = (type: 'play_video' | 'pause_video', assetIndex: number) =>
    timeline.log({ ref: answer.id, type, target: assetIndex.toString() })

  // The <video> unmounts on asset switch and product close, so its own pause event never reaches
  // React. Ref-based and idempotent: a delivered pause clears the ref, so nothing can double-log.
  const flushPlayingVideo = () => {
    if (null === playingIndexRef.current) return
    logVideoEvent('pause_video', playingIndexRef.current)
    playingIndexRef.current = null
  }

  const onVideoPlay = () => {
    playingIndexRef.current = selectedAssetIndex
    setIsPlaying(true)
    logVideoEvent('play_video', selectedAssetIndex)
  }

  const onVideoPause = () => {
    playingIndexRef.current = null
    setIsPlaying(false)
    logVideoEvent('pause_video', selectedAssetIndex)
  }
  const onLoadedMetadata = useCallback(async () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0.1
      await videoRef.current.play()
    }
  }, [])
  const resetImageState = useCallback(() => {
    requestAnimationFrame(() => {
      setOnImage(false)
      setPosition({ x: 0, y: 0 })
      timeline.log({
        ref: answer.id,
        type: 'zoom_image_out',
        target: selectedAssetIndex.toString()
      })
    })
  }, [selectedAssetIndex, timeline, answer.id])

  const onSmallAssetMouseEnter = (index: number) => {
    if (onImage) resetImageState()
    // a real switch flushes the playing video via the index effect; hovering the shown asset changes nothing
    if (index !== selectedAssetIndex) setSelectedAssetIndex(index)
  }
  const onBigAssetClick = useCallback(() => {
    if (onImage) {
      resetImageState()
    }
    timeline.log({
      ref: answer.id,
      type: 'zoom_image_in',
      target: selectedAssetIndex.toString()
    })
    setOnImage(state => !state)
  }, [resetImageState, onImage, answer.id, selectedAssetIndex, timeline])

  const onMoveOnImage = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!onImage) setOnImage(true)
      const element = event.currentTarget
      requestAnimationFrame(() => {
        const { left, top, width, height } = element.getBoundingClientRect()
        const centerX = width / 2
        const centerY = height / 2
        const mouseX = event.clientX - left
        const mouseY = event.clientY - top

        setPosition({
          x: -(mouseX - centerX) * 0.75,
          y: -(mouseY - centerY) * 0.75
        })
      })
    },
    [onImage]
  )

  const navigate = (direction: -1 | 1) => {
    if (-1 === direction) {
      if (0 === selectedAssetIndex) {
        setSelectedAssetIndex(assetsCollection.length - 1)
      } else {
        setSelectedAssetIndex(prevState => prevState - 1)
      }
    } else if (1 === direction) {
      if (selectedAssetIndex === assetsCollection.length - 1) {
        setSelectedAssetIndex(0)
      } else {
        setSelectedAssetIndex(prevState => prevState + 1)
      }
    }
  }

  useOnClickOutside(mainImageRef, () => {
    if (onImage) {
      setOnImage(false)
      resetImageState()
    }
  })

  // effects
  useEffect(() => {
    scrollTop()
  }, [scrollTop])

  // Declared before the show_asset effect so the closing pause lands ahead of the new asset's show_asset.
  useEffect(() => {
    flushPlayingVideo()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAssetIndex])

  useEffect(() => {
    if (!viewport.mobile) {
      timeline.log({
        ref: answer.id,
        type: 'show_asset',
        target: selectedAssetIndex.toString(),
        context: getAssetType(assetsCollection[selectedAssetIndex])?.toLowerCase()
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAssetIndex, viewport.mobile])

  useEffect(
    () => () => flushPlayingVideo(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return (
    <section className={`${style.product} ${viewport.mobile ? style.productMobile : ''}`}>
      {showModal && (
        <SubmitModal
          answer={answer}
          schema={schema}
          totals={totals}
          onCartOpen={onCartOpen}
          onModalClose={() => setShowModal(false)}
        />
      )}
      {viewport.mobile ? (
        <div className={style.carouselContainer}>
          <Carousel schema={answer} store="nike" pagination="default" />
        </div>
      ) : (
        <div className={style.images}>
          {assetsCollection.length > 1 && (
            <div className={style.imagesCarousel}>
              {assetsCollection.map((asset, index) => (
                <div
                  key={index}
                  className={`${style.itemPreview} ${
                    !answer.not_apply_filter && applyFilter(asset as IImageAsset) ? style.itemPreviewFilter : ''
                  }`}
                  onMouseEnter={() => onSmallAssetMouseEnter(index)}
                >
                  {index === selectedAssetIndex && <div className={style.activeLayout} />}
                  {ASSETS_TYPES.IMAGE === getAssetType(asset) && (
                    <div
                      className={style.imageAsset}
                      role="img"
                      aria-label={(asset as IImageAsset).alt_text || t('questions', 'img', 'noAlt')}
                      style={{
                        backgroundImage: `url(${getSmallAssetPath(asset as IImageAsset)})`
                      }}
                    />
                  )}
                  {ASSETS_TYPES.MODEL === getAssetType(asset) && (
                    <div className={style.fileAsset}>
                      <model-viewer
                        src={(asset as IFileAsset).file_url}
                        camera-controls={false}
                        interaction-prompt="none"
                        style={{ width: '100%', height: '100%' }}
                      />
                      <div className={style.layout} />
                    </div>
                  )}
                  {ASSETS_TYPES.VIDEO === getAssetType(asset) && (
                    <div
                      className={style.videoAsset}
                      role="presentation"
                      style={{
                        backgroundImage: `url(${(asset as IEcommerceVideoAsset).video.thumbnail})`
                      }}
                    >
                      <div className={style.playIcon}></div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className={style.mainImage}>
            {answer.best_seller && (
              <div className={nikeStyle.choice}>
                <div className={nikeStyle.choiceIcon} />
                <span className={nikeStyle.choiceText}>{t('questions', 'ec', 'nike', 'highlyRated')}</span>
              </div>
            )}
            {assetsCollection.length > 1 && (
              <div className={style.navigation}>
                <button className={style.nav} onClick={() => navigate(-1)}>
                  <div className={style.prevIcon} />
                </button>
                <button className={style.nav} onClick={() => navigate(1)}>
                  <div className={style.nextIcon} />
                </button>
              </div>
            )}
            {assetsCollection[selectedAssetIndex] ? (
              <div
                className={`${style.bigImage} ${
                  !answer.not_apply_filter &&
                  ASSETS_TYPES.IMAGE === getAssetType(assetsCollection[selectedAssetIndex]) &&
                  applyFilter(assetsCollection[selectedAssetIndex] as IImageAsset)
                    ? style.bigImageFilter
                    : ''
                }`}
              >
                {ASSETS_TYPES.IMAGE === getAssetType(assetsCollection[selectedAssetIndex]) && (
                  <div
                    ref={mainImageRef}
                    className={style.imageAsset}
                    role="img"
                    aria-label={
                      (assetsCollection[selectedAssetIndex] as IImageAsset).alt_text || t('questions', 'img', 'noAlt')
                    }
                    style={{
                      backgroundImage: `url(${getBigAssetPath(assetsCollection[selectedAssetIndex] as IImageAsset)})`,
                      backgroundPosition: position
                        ? `calc(50% + ${position.x}px) calc(50% + ${position.y}px)`
                        : '50% 50%',
                      transform: `scale(${onImage ? 2 : 1})`,
                      cursor: onImage ? 'zoom-out' : 'zoom-in'
                    }}
                    onMouseMove={event => {
                      if (onImage) onMoveOnImage(event)
                    }}
                    onClick={onBigAssetClick}
                  />
                )}
                {ASSETS_TYPES.MODEL === getAssetType(assetsCollection[selectedAssetIndex]) && (
                  <model-viewer
                    src={(assetsCollection[selectedAssetIndex] as IFileAsset).file_url}
                    camera-controls={false}
                    style={{ width: '100%', height: '100%' }}
                  />
                )}
                {ASSETS_TYPES.VIDEO === getAssetType(assetsCollection[selectedAssetIndex]) && (
                  <div className={style.videoWrapper}>
                    <video
                      ref={videoRef}
                      className={style.videoAsset}
                      src={(assetsCollection[selectedAssetIndex] as IEcommerceVideoAsset).video.url}
                      preload="auto"
                      controls={false}
                      playsInline
                      muted
                      onPlay={onVideoPlay}
                      onPause={onVideoPause}
                      onLoadedMetadata={() => void onLoadedMetadata()}
                    />
                    <button className={style.videoControls} onClick={() => void togglePlayPause()}>
                      <div className={isPlaying ? style.pause : style.play} />
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className={style.noImage} />
            )}
          </div>
        </div>
      )}
      <div className={style.description}>
        <div className={style.caption} dangerouslySetInnerHTML={{ __html: answer.text }} />
        {answer.best_seller && viewport.mobile && (
          <div className={style.choice}>
            <div className={style.choiceIcon} />
            <span className={style.choiceText}>{t('questions', 'ec', 'nike', 'highlyRated')}</span>
          </div>
        )}
        <div className={style.prices}>
          {answer.discounted_price && answer.price > answer.discounted_price ? (
            <>
              <div
                className={style.newPrice}
                dangerouslySetInnerHTML={{
                  __html: `${formatPriceWithLocale(answer.discounted_price, schema.price_mask, languageTag, true, false)}`
                }}
              />
              <div
                className={style.lastPrice}
                dangerouslySetInnerHTML={{
                  __html: `${formatPriceWithLocale(answer.price, schema.price_mask, languageTag, true, false)}`
                }}
              />
              <div
                className={style.off}
                dangerouslySetInnerHTML={{
                  __html: t<(off: string, applied: boolean) => string>(
                    'questions',
                    'ec',
                    'nike',
                    'off'
                  )(
                    (
                      ((schema.answers[index].price - schema.answers[index].discounted_price) /
                        schema.answers[index].price) *
                      100
                    ).toFixed(1),
                    false
                  )
                }}
              />
            </>
          ) : (
            <div
              className={style.defaultPrice}
              dangerouslySetInnerHTML={{
                __html: `${formatPriceWithLocale(answer.price, schema.price_mask, languageTag, true, false)}`
              }}
            />
          )}
        </div>
        {answer.item_size && (
          <div className={style.sizes}>
            <div className={style.head}>
              <div className={style.select}>{t('questions', 'ec', 'nike', 'select')}</div>
              <EmptyLink color="#111">
                <div className={style.guide}>
                  <div className={style.guideIcon} />
                  <span className={style.guideText}>{t('questions', 'ec', 'nike', 'guide')}</span>
                </div>
              </EmptyLink>
            </div>
            <div className={style.sizeList}>
              {'clothing' === answer.item_size ? (
                <>
                  {CLOTHING.map((item, index) => (
                    <div key={index} className={style.sizeItem}>
                      <EmptyLink color="#111">
                        <div className={style.innerItem}>{item}</div>
                      </EmptyLink>
                    </div>
                  ))}
                </>
              ) : (
                <>
                  {FOOTWEAR.map((item, index) => (
                    <div key={index} className={style.sizeItem}>
                      <EmptyLink color="#111">
                        <div className={style.innerItem}>{item}</div>
                      </EmptyLink>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        )}
        <button
          disabled={findability && quantity > 0}
          className={`${style.add} ${
            schema.max_qty_per_product && quantity >= schema.max_qty_per_product ? style.disabled : ''
          }`}
          onClick={() => {
            hapticEffects.selection()
            onAddToCart(1, index)
            if (findability) {
              checkout()
            } else {
              setShowModal(true)
            }
          }}
        >
          {t('questions', 'ec', 'nike', 'addToCart')}
        </button>
        <EmptyLink color="#111">
          <div className={style.fav}>
            {t('questions', 'ec', 'nike', 'favTitle')}
            <div className={style.heartIcon} />
          </div>
        </EmptyLink>
        <div className={style.shippingTitle}>{t('questions', 'ec', 'nike', 'shipping')}</div>
        <div
          className={style.shippingText}
          dangerouslySetInnerHTML={{ __html: t('questions', 'ec', 'nike', 'shippingText') }}
        />
        <div className={style.pickupTitle}>{t('questions', 'ec', 'nike', 'freePickup')}</div>
        <EmptyLink color="#111">
          <div className={style.findStore}>{t('questions', 'ec', 'nike', 'findStore')}</div>
        </EmptyLink>
        <div
          className={style.answerText}
          dangerouslySetInnerHTML={{
            __html: `${answer.feature_bullets} ${answer.description}`
              .replace(/&lt;ul&gt;/g, '<ul>')
              .replace(/&lt;\/ul&gt;/g, '</ul>')
              .replace(/&lt;li&gt;/g, '<li>')
              .replace(/&lt;\/li&gt;/g, '</li>')
          }}
        />
        <EmptyLink color="#111">
          <div className={style.findStore}>{t('questions', 'ec', 'nike', 'details')}</div>
        </EmptyLink>
        <EmptyLink color="#111">
          <div className={style.additional}>
            <div className={style.text}>{t('questions', 'ec', 'nike', 'shipAndReturn')}</div>
            <div className={style.downIcon} />
          </div>
        </EmptyLink>
        <EmptyLink color="#111">
          <div className={style.additional}>
            <div className={style.text}>{t('questions', 'ec', 'nike', 'made')}</div>
            <div className={style.downIcon} />
          </div>
        </EmptyLink>
        <EmptyLink color="#111">
          <div className={style.additional}>
            <div
              className={style.text}
              dangerouslySetInnerHTML={{
                __html: `${t('questions', 'ec', 'nike', 'reviews')} (${answer.reviews})`
              }}
            />
            <div className={style.rating}>
              <Rating rating={answer.rating} />
            </div>
            <div className={style.downIcon} />
          </div>
        </EmptyLink>
      </div>
    </section>
  )
}

export default Product
